/**
 * PBFCM-PDP Controller
 * Black Friday Campaign - Mobile Product Detail Page JavaScript Controller
 *
 * Handles all user interactions and state management for PBFCM-PDP template
 * Based on patterns from separate-buy-buttons-v4-no-bulk.liquid
 */

// Initialize global namespace
window.PBFCM_PDP = window.PBFCM_PDP || {};

(function(PBFCM_PDP) {
  'use strict';

  // Configuration - All settings and constants
  const CONFIG = {
    // Selling Plans
    STAY_SELLING_PLAN_ID: null, // Will be set from hidden field
    ADD_ON_SUBSCRIPTION_PLAN_ID: null,

    // Prices and Discounts (will be updated from product data)
    SUBSCRIPTION_DISCOUNT: 0.64,
    SHIPPING_COST: 5.00,
    BFCM_MODE_ENABLED: false,
    BFCM_DISCOUNT_PERCENT: 0,
    BFCM_DISCOUNT_MULTIPLIER: 1,

    // Product Properties (matches separate-buy-buttons-v4-no-bulk pattern)
    PROPERTIES: {
      FULL_PRICE: "_full_price",
      PRODUCT_TYPE: "_product_type",
      SUBSCRIPTION_PURCHASE: "_is_subscription_purchase",
      ONE_TIME_PURCHASE: "_is_one_time_purchase",
      UPSELL_ITEM: "_is_upsell",
      BFCM_ADD_ON_PROMO_PRICE: "_bfcm_add_on_promo_price",
      BFCM_ADD_ON_FULL_PRICE: "_bfcm_add_on_full_price"
    },

    // Element IDs
    IDS: {
      FREQUENCY_MONTHLY: 'PBFCM-frequency-monthly',
      FREQUENCY_ONETIME: 'PBFCM-frequency-onetime',
      RADIO_MONTHLY: 'PBFCM-radio-monthly',
      RADIO_ONETIME: 'PBFCM-radio-onetime',
      CHECKMARK_MONTHLY: 'PBFCM-checkmark-monthly',
      CHECKMARK_ONETIME: 'PBFCM-checkmark-onetime',
      SELECTED_QUANTITY: 'PBFCM-selected-quantity',
      VARIANT_ID: 'PBFCM-variant-id',
      PRODUCT_ID: 'PBFCM-product-id',
      SELLING_PLAN_ID: 'PBFCM-selling-plan-id',
      ADDON_PLAN_ID: 'PBFCM-addon-plan-id',
      BASE_PRICE: 'PBFCM-base-price',
      SUBSCRIPTION_DISCOUNT: 'PBFCM-subscription-discount',
      ADD_TO_CART_BTN: 'PBFCM-add-to-cart-btn',
      CART_LOADING: 'PBFCM-cart-loading',
      COUPON_CODE: 'PBFCM-coupon-code',
      COPY_MESSAGE: 'PBFCM-copy-message',
      BFCM_MODE: 'PBFCM-bfcm-mode-enabled',
      BFCM_DISCOUNT_PERCENT: 'PBFCM-bfcm-discount-percent'
    },

    // Class Names
    CLASSES: {
      OPTION_SELECTED: 'PBFCM-PDP-frequency-selector__option--selected',
      QUANTITY_SELECTED: 'PBFCM-PDP-quantity-selector__item--selected',
      SAVINGS_SELECTED: 'PBFCM-PDP-quantity-selector__savings-badge--selected'
    }
  };

  // State management
  const state = {
    frequency: 'monthly', // 'monthly' or 'onetime'
    quantity: 2, // Default quantity
    upsells: {}, // { productId: quantity }
    prices: {
      base: 0,
      subscription: 0,
      onetime: 0,
      shipping: CONFIG.SHIPPING_COST,
      total: 0,
      savings: 0
    },
    variantId: null,
    productId: null,
    sellingPlanId: null
  };

  /**
   * Initialize the controller
   */
  function init() {
    console.log('🚀 [PBFCM-PDP] Initializing controller');

    // Load configuration from hidden fields
    loadConfig();

    // Calculate initial prices
    calculateTotals();

    // Set up event listeners
    setupEventListeners();
    updateQuantitySavingsDisplay();

    console.log('✅ [PBFCM-PDP] Controller initialized', state);
  }

  /**
   * Load configuration from hidden fields
   */
  function loadConfig() {
    const variantIdEl = document.getElementById(CONFIG.IDS.VARIANT_ID);
    const productIdEl = document.getElementById(CONFIG.IDS.PRODUCT_ID);
    const sellingPlanIdEl = document.getElementById(CONFIG.IDS.SELLING_PLAN_ID);
    const addonPlanIdEl = document.getElementById(CONFIG.IDS.ADDON_PLAN_ID);
    const basePriceEl = document.getElementById(CONFIG.IDS.BASE_PRICE);
    const subscriptionDiscountEl = document.getElementById(CONFIG.IDS.SUBSCRIPTION_DISCOUNT);
    const selectedQuantityEl = document.getElementById(CONFIG.IDS.SELECTED_QUANTITY);
    const bfcmModeEl = document.getElementById(CONFIG.IDS.BFCM_MODE);
    const bfcmDiscountEl = document.getElementById(CONFIG.IDS.BFCM_DISCOUNT_PERCENT);

    if (variantIdEl) state.variantId = parseInt(variantIdEl.value);
    if (productIdEl) state.productId = parseInt(productIdEl.value);
    if (sellingPlanIdEl) {
      state.sellingPlanId = sellingPlanIdEl.value;
      CONFIG.STAY_SELLING_PLAN_ID = sellingPlanIdEl.value;
    }
    if (addonPlanIdEl) {
      CONFIG.ADD_ON_SUBSCRIPTION_PLAN_ID = addonPlanIdEl.value || null;
      console.log('📦 [PBFCM-PDP] Addon plan ID from hidden field:', CONFIG.ADD_ON_SUBSCRIPTION_PLAN_ID);
    } else {
      console.warn('⚠️ [PBFCM-PDP] Hidden field PBFCM-addon-plan-id not found');
    }

    // Add warning if addon plan is not set
    if (!CONFIG.ADD_ON_SUBSCRIPTION_PLAN_ID) {
      console.warn('⚠️ [PBFCM-PDP] ADD_ON_SUBSCRIPTION_PLAN_ID is not configured - add-on subscriptions will not work correctly');
      console.warn('⚠️ [PBFCM-PDP] Please configure settings.addon_subscription_plan_id in Shopify admin');
    }

    // Load starter metafields
    const starterMultivitaminEl = document.getElementById('PBFCM-starter-multivitamin');
    const starterMagnesiumEl = document.getElementById('PBFCM-starter-magnesium');
    const starterBioticEl = document.getElementById('PBFCM-starter-biotic');

    CONFIG.STARTER_METAFIELDS = {
      multivitamin: starterMultivitaminEl ? starterMultivitaminEl.value : '',
      magnesium: starterMagnesiumEl ? starterMagnesiumEl.value : '',
      biotic: starterBioticEl ? starterBioticEl.value : ''
    };

    console.log('📦 [PBFCM-PDP] Starter metafields loaded:', CONFIG.STARTER_METAFIELDS);

    if (basePriceEl) state.prices.base = parseFloat(basePriceEl.value) / 100;
    if (subscriptionDiscountEl) CONFIG.SUBSCRIPTION_DISCOUNT = parseFloat(subscriptionDiscountEl.value);
    if (selectedQuantityEl) state.quantity = parseInt(selectedQuantityEl.value);
    if (bfcmModeEl) {
      const modeValue = bfcmModeEl.value;
      CONFIG.BFCM_MODE_ENABLED = modeValue === 'true' || modeValue === '1';
    }
    if (bfcmDiscountEl) {
      const percentValue = parseFloat(bfcmDiscountEl.value);
      if (!isNaN(percentValue)) {
        CONFIG.BFCM_DISCOUNT_PERCENT = percentValue;
        CONFIG.BFCM_DISCOUNT_MULTIPLIER = Math.max((100 - percentValue) / 100, 0);
      }
    }

    console.log('📋 [PBFCM-PDP] Config loaded:', {
      variantId: state.variantId,
      productId: state.productId,
      sellingPlanId: state.sellingPlanId,
      basePrice: state.prices.base,
      subscriptionDiscount: CONFIG.SUBSCRIPTION_DISCOUNT,
      bfcmModeEnabled: CONFIG.BFCM_MODE_ENABLED,
      bfcmDiscountPercent: CONFIG.BFCM_DISCOUNT_PERCENT
    });
  }

  /**
   * Set up event listeners
   */
  function setupEventListeners() {
    // Radio button change listeners
    const monthlyRadio = document.getElementById(CONFIG.IDS.RADIO_MONTHLY);
    const onetimeRadio = document.getElementById(CONFIG.IDS.RADIO_ONETIME);

    if (monthlyRadio) {
      monthlyRadio.addEventListener('change', function() {
        if (this.checked) selectFrequency('monthly');
      });
    }

    if (onetimeRadio) {
      onetimeRadio.addEventListener('change', function() {
        if (this.checked) selectFrequency('onetime');
      });
    }
  }

  /**
   * Select purchase frequency (monthly subscription or one-time)
   * @param {string} frequency - 'monthly' or 'onetime'
   */
  function selectFrequency(frequency) {
    console.log('🔄 [PBFCM-PDP] Frequency changed to:', frequency);

    state.frequency = frequency;
    const isMonthly = frequency === 'monthly';

    // Update UI
    const monthlyOption = document.getElementById(CONFIG.IDS.FREQUENCY_MONTHLY);
    const onetimeOption = document.getElementById(CONFIG.IDS.FREQUENCY_ONETIME);
    const monthlyCheckmark = document.getElementById(CONFIG.IDS.CHECKMARK_MONTHLY);
    const onetimeCheckmark = document.getElementById(CONFIG.IDS.CHECKMARK_ONETIME);
    const monthlyRadio = document.getElementById(CONFIG.IDS.RADIO_MONTHLY);
    const onetimeRadio = document.getElementById(CONFIG.IDS.RADIO_ONETIME);

    // Get benefits and badge elements
    const monthlyBenefits = document.getElementById('PBFCM-monthly-benefits');
    const onetimeBenefits = document.getElementById('PBFCM-onetime-benefits');
    const onetimeBadge = document.getElementById('PBFCM-onetime-badge');

    if (isMonthly) {
      // Select monthly
      if (monthlyOption) {
        monthlyOption.classList.add(CONFIG.CLASSES.OPTION_SELECTED);
        monthlyOption.classList.remove('PBFCM-PDP-frequency-selector__option--dimmed');
        monthlyOption.style.border = '';
      }
      if (onetimeOption) {
        onetimeOption.classList.remove(CONFIG.CLASSES.OPTION_SELECTED);
        onetimeOption.classList.remove('PBFCM-PDP-frequency-selector__option--onetime-selected');
      }
      if (monthlyCheckmark) monthlyCheckmark.style.display = 'inline-flex';
      if (onetimeCheckmark) onetimeCheckmark.style.display = 'none';
      if (monthlyRadio) monthlyRadio.checked = true;
      if (onetimeRadio) onetimeRadio.checked = false;

      // Show monthly benefits, hide one-time negative benefits
      if (monthlyBenefits) monthlyBenefits.classList.remove('PBFCM-PDP-frequency-selector__benefits--hidden');
      if (onetimeBenefits) onetimeBenefits.classList.add('PBFCM-PDP-frequency-selector__benefits--hidden');
      if (onetimeBadge) onetimeBadge.classList.add('PBFCM-PDP-frequency-selector__badge--hidden');
    } else {
      // Select one-time
      if (monthlyOption) {
        monthlyOption.classList.remove(CONFIG.CLASSES.OPTION_SELECTED);
        monthlyOption.classList.add('PBFCM-PDP-frequency-selector__option--dimmed');
        monthlyOption.style.border = '1px solid var(--color-helper-outline, #D7EFFA)';
      }
      if (onetimeOption) {
        onetimeOption.classList.add(CONFIG.CLASSES.OPTION_SELECTED);
        onetimeOption.classList.add('PBFCM-PDP-frequency-selector__option--onetime-selected');
      }
      if (monthlyCheckmark) monthlyCheckmark.style.display = 'none';
      if (onetimeCheckmark) onetimeCheckmark.style.display = 'inline-flex';
      if (monthlyRadio) monthlyRadio.checked = false;
      if (onetimeRadio) onetimeRadio.checked = true;

      // Hide monthly benefits, show one-time negative benefits
      if (monthlyBenefits) monthlyBenefits.classList.remove('PBFCM-PDP-frequency-selector__benefits--hidden');
      if (onetimeBenefits) onetimeBenefits.classList.remove('PBFCM-PDP-frequency-selector__benefits--hidden');
      if (onetimeBadge) onetimeBadge.classList.remove('PBFCM-PDP-frequency-selector__badge--hidden');
    }

    // Update CSS variable based on selection
    const root = document.documentElement;
    if (frequency === 'onetime') {
      root.style.setProperty('--PBFCM-color-success', '#476ded');
    } else {
      root.style.setProperty('--PBFCM-color-success', '#078942');
    }

    // Recalculate totals
    updateQuantitySavingsDisplay();
    calculateTotals();
  }

  /**
   * Select quantity
   * @param {number} qty - Quantity to select (1-4)
   */
  function selectQuantity(qty) {
    console.log('🔢 [PBFCM-PDP] Quantity changed to:', qty);

    state.quantity = qty;

    // Update hidden field
    const selectedQuantityEl = document.getElementById(CONFIG.IDS.SELECTED_QUANTITY);
    if (selectedQuantityEl) selectedQuantityEl.value = qty;

    // Update UI - remove selection from all items
    for (let i = 1; i <= 4; i++) {
      const item = document.getElementById(`PBFCM-quantity-${i}`);
      if (item) {
        item.classList.remove(CONFIG.CLASSES.QUANTITY_SELECTED);
        item.style.borderColor = '';

        // Also update savings badge
        const savingsBadge = item.querySelector('.PBFCM-PDP-quantity-selector__savings-badge');
        if (savingsBadge) {
          savingsBadge.classList.remove(CONFIG.CLASSES.SAVINGS_SELECTED);
          savingsBadge.style.backgroundColor = '';
        }
      }
    }

    // Add selection to chosen item
    const selectedItem = document.getElementById(`PBFCM-quantity-${qty}`);
    if (selectedItem) {
      selectedItem.classList.add(CONFIG.CLASSES.QUANTITY_SELECTED);
      selectedItem.style.borderColor = 'var(--PBFCM-color-success)';

      // Also update savings badge
      const savingsBadge = selectedItem.querySelector('.PBFCM-PDP-quantity-selector__savings-badge');
      if (savingsBadge) {
        savingsBadge.classList.add(CONFIG.CLASSES.SAVINGS_SELECTED);
        savingsBadge.style.backgroundColor = 'var(--PBFCM-color-success)';
      }
    }

    // Recalculate totals
    calculateTotals();
  }

  function updateQuantitySavingsDisplay() {
    const isSubscription = state.frequency === 'monthly';
    const savingsElements = document.querySelectorAll('.PBFCM-PDP-quantity-selector__savings-text');
    savingsElements.forEach(element => {
      const nextText = isSubscription ? element.dataset.subscriptionText : element.dataset.onetimeText;
      if (nextText) {
        element.textContent = nextText;
      }
    });
  }

  /**
   * Update upsell product quantity
   * @param {string|number} productId - Product ID
   * @param {number} change - Change in quantity (+1 or -1)
   */
  function updateUpsellQuantity(productId, change) {
    console.log('🎁 [PBFCM-PDP] Updating upsell quantity:', productId, change);

    const currentQty = state.upsells[productId] || 0;
    let newQty = currentQty + change;

    // Get max quantity from data attribute
    const upsellCard = document.querySelector(`[data-product-id="${productId}"]`);
    const upsellData = upsellCard?.querySelector('.PBFCM-upsell-data');
    const maxQty = upsellData ? parseInt(upsellData.dataset.max) : 5;

    // Clamp between 0 and max
    newQty = Math.max(0, Math.min(newQty, maxQty));

    // Update state
    if (newQty === 0) {
      delete state.upsells[productId];
    } else {
      state.upsells[productId] = newQty;
    }

    // Update display
    const qtyDisplay = document.getElementById(`PBFCM-upsell-qty-${productId}`);
    if (qtyDisplay) qtyDisplay.textContent = newQty;

    // Update hidden field
    if (upsellData) upsellData.value = newQty;

    // Update minus button opacity
    const minusBtn = upsellCard?.querySelector('[data-action="decrease"]');
    if (minusBtn) {
      minusBtn.style.opacity = newQty === 0 ? '0.3' : '1';
    }

    console.log('🎁 [PBFCM-PDP] Upsell quantities:', state.upsells);

    // Recalculate totals
    calculateTotals();
  }

  function getUpsellPriceData(upsellData) {
    if (!upsellData) {
      return {
        priceCents: 0,
        comparePriceCents: 0,
        price: 0,
        comparePrice: 0
      };
    }

    const rawPriceCents = parseInt(upsellData.dataset.price, 10);
    const rawCompareCents = parseInt(upsellData.dataset.comparePrice || upsellData.dataset.price, 10);
    const priceCents = Number.isFinite(rawPriceCents) ? rawPriceCents : 0;
    const comparePriceCents = Number.isFinite(rawCompareCents) ? rawCompareCents : priceCents;

    return {
      priceCents,
      comparePriceCents,
      price: priceCents / 100,
      comparePrice: comparePriceCents / 100
    };
  }

  /**
   * Parse money string to cents
   * @param {string|number} value - Money value (e.g., "$12.34" or "12.34" or 12.34)
   * @returns {number|null} - Value in cents or null if invalid
   */
  function parseMoneyToCents(value) {
    if (value === undefined || value === null) return null;
    const textValue = value.toString();
    if (textValue.toUpperCase().includes('FREE')) return 0;
    const normalized = textValue.replace(/[^0-9.,]/g, '').replace(/,/g, '');
    if (!normalized) return null;
    const floatValue = parseFloat(normalized);
    if (isNaN(floatValue)) return null;
    return Math.round(floatValue * 100);
  }

  /**
   * Get total addon subtotal in cents
   * @returns {number} - Addon subtotal in cents
   */
  function getAddonSubtotalCents() {
    let addonTotal = 0;
    Object.keys(state.upsells).forEach(productId => {
      const qty = state.upsells[productId];
      if (qty > 0) {
        const upsellData = document.querySelector(`[data-upsell-id="${productId}"]`);
        if (upsellData && upsellData.dataset.isAddon === 'true') {
          const priceData = getUpsellPriceData(upsellData);
          addonTotal += Math.round(priceData.price * qty * 100);
        }
      }
    });
    return addonTotal;
  }

  /**
   * Dispatch summary updated event for gift progress component
   * @param {number} totalSaleCents - Total sale price in cents
   */
  function dispatchSummaryUpdatedEvent(totalSaleCents) {
    document.dispatchEvent(new CustomEvent('pbfcmpdp:summary-updated', {
      detail: {
        allItemsSubtotalCents: totalSaleCents,
        addonSubtotalCents: getAddonSubtotalCents(),
        shippingCents: state.frequency === 'monthly' ? 0 : CONFIG.SHIPPING_COST * 100
      }
    }));
    console.log('📢 [PBFCM-PDP] Dispatched pbfcmpdp:summary-updated event');
  }

  /**
   * Calculate all prices and update displays
   * Simplified to read from DOM elements with name attributes
   */
  function calculateTotals() {
    console.log('💰 [PBFCM-PDP] Calculating totals from DOM');

    const isSubscription = state.frequency === 'monthly';
    const basePrice = state.prices.base;

    // Calculate subscription and onetime prices
    state.prices.subscription = basePrice * CONFIG.SUBSCRIPTION_DISCOUNT;
    state.prices.onetime = basePrice;

    // First, ensure all individual items are rendered
    updatePriceDisplays();

    // Wait a tick for DOM to update
    requestAnimationFrame(() => {
      // Sum all compare prices (original prices)
      const compareElements = document.querySelectorAll('[name="cart-summary-compare-price"]');
      let totalCompare = 0;
      compareElements.forEach(el => {
        const cents = parseMoneyToCents(el.textContent);
        if (cents !== null) totalCompare += cents;
      });

      // Sum all sale prices (discounted prices)
      const saleElements = document.querySelectorAll('[name="cart-summary-sale-price"]');
      let totalSale = 0;
      saleElements.forEach(el => {
        const cents = parseMoneyToCents(el.textContent);
        if (cents !== null) totalSale += cents;
      });

      // Update subtotal displays
      const subtotalCompare = document.getElementById('PBFCM-breakdown-subtotal-compare');
      const subtotalFinal = document.getElementById('PBFCM-breakdown-subtotal-final');
      if (subtotalCompare) subtotalCompare.textContent = formatMoney(totalCompare / 100);
      if (subtotalFinal) subtotalFinal.textContent = formatMoney(totalSale / 100);

      // Update state
      state.prices.total = totalSale / 100;
      state.prices.savings = (totalCompare - totalSale) / 100;

      // Update savings badge
      const savingsText = document.getElementById('PBFCM-breakdown-savings');
      if (savingsText) {
        savingsText.textContent = `You are saving a total of ${formatMoney(state.prices.savings)}!`;
      }

      // Dispatch event for gift progress
      dispatchSummaryUpdatedEvent(totalSale);

      console.log('💰 [PBFCM-PDP] Totals calculated:', {
        totalCompare: totalCompare / 100,
        totalSale: totalSale / 100,
        savings: state.prices.savings
      });
    });
  }

  /**
   * Update all price displays in the UI
   */
  function updatePriceDisplays() {
    const isSubscription = state.frequency === 'monthly';
    const basePrice = state.prices.base;
    const unitPrice = isSubscription ? state.prices.subscription : state.prices.onetime;
    const mainProductTotal = unitPrice * state.quantity;
    const mainProductOriginalTotal = basePrice * state.quantity;

    // Update breakdown product line
    const productLabel = document.getElementById('PBFCM-breakdown-product-label');
    const productCompare = document.getElementById('PBFCM-breakdown-product-compare');
    const productFinal = document.getElementById('PBFCM-breakdown-product-final');

    // Get product title from page or default
    const productTitle = document.querySelector('.PBFCM-PDP-cart-summary__line-label--product')?.textContent.split('(')[0].trim() || "Kids' Multivitamin";

    if (productLabel) productLabel.textContent = `${productTitle} (${state.quantity}x)`;
    if (productCompare) productCompare.textContent = formatMoney(mainProductOriginalTotal);
    if (productFinal) productFinal.textContent = formatMoney(applyBfcmDiscount(mainProductTotal));

    // Update upsell lines in breakdown
    renderUpsellLines();

    // Update breakdown shipping
    const shippingAmount = isSubscription ? 0 : CONFIG.SHIPPING_COST;
    const shippingCompare = document.getElementById('PBFCM-breakdown-shipping-compare');
    const shippingFinal = document.getElementById('PBFCM-breakdown-shipping-final');
    if (shippingCompare) shippingCompare.textContent = `$${CONFIG.SHIPPING_COST.toFixed(2)}`;
    if (shippingFinal) shippingFinal.textContent = isSubscription ? 'FREE' : `$${CONFIG.SHIPPING_COST.toFixed(2)}`;
  }

  /**
   * Render upsell lines in the cart summary breakdown
   */
  function renderUpsellLines() {
    const container = document.getElementById('PBFCM-breakdown-upsells-container');
    if (!container) return;

    // Clear existing upsell lines
    container.innerHTML = '';
    container.style.display = 'none';

    // Check if there are any upsells
    const hasUpsells = Object.keys(state.upsells).some(productId => state.upsells[productId] > 0);
    if (!hasUpsells) return;

    // Get success color from the page
    const successColor = getComputedStyle(document.documentElement).getPropertyValue('--success-color') || '#078942';

    // Render each upsell
    Object.keys(state.upsells).forEach(productId => {
      const qty = state.upsells[productId];
      if (qty === 0) return;

      const upsellData = document.querySelector(`[data-upsell-id="${productId}"]`);
      console.log("[renderUpsellLines] upsell data", upsellData.dataset);
      if (!upsellData) return;

      const upsellTitle = upsellData.dataset.title || 'Upsell Product';
      const priceData = getUpsellPriceData(upsellData);
      const isAddOn = upsellData.dataset.isAddon === 'true';
      const unitPrice = priceData.price;
      const originalPrice = priceData.comparePrice;

      const totalPrice = unitPrice * qty;
      const totalOriginalPrice = originalPrice * qty;
      const discountedTotalPrice =totalPrice;
      console.log("[renderUpsellLines], totalPrice", totalPrice)
        console.log("[renderUpsellLines], discountedTotalPrice", discountedTotalPrice)
        console.log("[renderUpsellLines], totalOriginalPrice", totalOriginalPrice)

      // Create upsell line HTML
      const upsellLineHTML = `
        <div class="PBFCM-PDP-cart-summary__line">
          <span class="PBFCM-PDP-cart-summary__line-label PBFCM-PDP-cart-summary__line-label--product">
            ${upsellTitle} (${qty}x)
          </span>
          <div class="PBFCM-PDP-cart-summary__line-price">
            <span class="PBFCM-PDP-cart-summary__line-compare" name="cart-summary-compare-price">
              ${formatMoney(totalOriginalPrice)}
            </span>
            <span class="PBFCM-PDP-cart-summary__line-final" style="color: ${successColor};" name="cart-summary-sale-price">
              ${formatMoney(discountedTotalPrice)}
            </span>
          </div>
        </div>
      `;

      container.insertAdjacentHTML('beforeend', upsellLineHTML);
    });

    container.style.display = '';
  }

  function getBfcmDiscountMultiplier() {
    if (!CONFIG.BFCM_MODE_ENABLED) return 1;
    if (typeof CONFIG.BFCM_DISCOUNT_MULTIPLIER !== 'number' || CONFIG.BFCM_DISCOUNT_MULTIPLIER <= 0) {
      return 1;
    }
    return CONFIG.BFCM_DISCOUNT_MULTIPLIER;
  }

  function applyBfcmDiscount(amount) {
    if (typeof amount !== 'number') return amount;
    const multiplier = getBfcmDiscountMultiplier();
    return amount * multiplier;
  }

  /**
   * Format price as money string
   * @param {number} amount - Amount in dollars
   * @returns {string} Formatted money string
   */
  function formatMoney(amount) {
    return `$${amount.toFixed(2)}`;
  }

  /**
   * Copy coupon code to clipboard
   */
  function copyCouponCode() {
    const couponCodeEl = document.getElementById(CONFIG.IDS.COUPON_CODE);
    const copyMessageEl = document.getElementById(CONFIG.IDS.COPY_MESSAGE);

    if (!couponCodeEl) return;

    const code = couponCodeEl.textContent;

    // Copy to clipboard
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code)
        .then(() => {
          console.log('📋 [PBFCM-PDP] Coupon code copied:', code);
          showCopyMessage();
        })
        .catch(err => {
          console.error('❌ [PBFCM-PDP] Failed to copy:', err);
          fallbackCopy(code);
        });
    } else {
      fallbackCopy(code);
    }
  }

  /**
   * Fallback copy method for older browsers
   * @param {string} text - Text to copy
   */
  function fallbackCopy(text) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();

    try {
      document.execCommand('copy');
      console.log('📋 [PBFCM-PDP] Coupon code copied (fallback):', text);
      showCopyMessage();
    } catch (err) {
      console.error('❌ [PBFCM-PDP] Fallback copy failed:', err);
    }

    document.body.removeChild(textArea);
  }

  /**
   * Show copy success message
   */
  function showCopyMessage() {
    const copyMessageEl = document.getElementById(CONFIG.IDS.COPY_MESSAGE);
    if (!copyMessageEl) return;

    copyMessageEl.style.display = 'block';

    setTimeout(() => {
      copyMessageEl.style.display = 'none';
    }, 2000);
  }

  /**
   * Create item data structure for cart
   * Uses CartUtilities if available, otherwise falls back to local implementation
   */
  function createItemData({ variantId, quantity, isSubscription, sellingPlanId = null, properties = {}, isAddon = false }) {
    console.log('📦 [PBFCM-PDP] Creating item data:', { variantId, quantity, isSubscription, isAddon });

    // Use CartUtilities if available
    if (window.CartUtilities && typeof window.CartUtilities.createItemData === 'function') {
      return window.CartUtilities.createItemData({
        variantId,
        quantity,
        isSubscription,
        properties,
          sellingPlanId,
        isAddon
      });
    }
      console.log('📦 [PBFCM-PDP] Creating item data2:', { variantId, quantity, isSubscription, isAddon });

    // Fallback to local implementation
    const baseProperties = {
      [CONFIG.PROPERTIES.FULL_PRICE]: "true",
      [CONFIG.PROPERTIES.PRODUCT_TYPE]: "rebrand",
      ...properties
    };

    const item = {
      id: variantId,
      quantity: quantity,
      properties: baseProperties
    };

    if (isSubscription) {
      // If sellingPlanId is explicitly provided, use it
      // Otherwise, use addon plan for add-ons, or Stay AI plan for regular products
      const planIdToUse = sellingPlanId ||
                         (isAddon ? CONFIG.ADD_ON_SUBSCRIPTION_PLAN_ID : CONFIG.STAY_SELLING_PLAN_ID);
      if (planIdToUse) {
        item.selling_plan = planIdToUse;
      }
      item.properties[CONFIG.PROPERTIES.SUBSCRIPTION_PURCHASE] = true;
    } else {
      item.properties[CONFIG.PROPERTIES.ONE_TIME_PURCHASE] = true;
    }

    console.log('📦 [PBFCM-PDP] Item data created:', item);
    return item;
  }

  /**
   * Main add to cart handler
   * Based on separate-buy-buttons-v4-no-bulk.liquid handleAddToCart function
   */
  async function handleAddToCart() {
    console.log('🛒 [PBFCM-PDP] Starting add to cart');

    // Show loading state
    setLoading(true);

    try {
      const isSubscription = state.frequency === 'monthly';
      const items = [];

      // Check if product has starter variants
      const starterVariants = [
        CONFIG.STARTER_METAFIELDS.multivitamin,
        CONFIG.STARTER_METAFIELDS.magnesium,
        CONFIG.STARTER_METAFIELDS.biotic
      ].filter(variantId => variantId && variantId !== '');

      if (starterVariants.length > 0) {
        // Product has starter metafields - ONLY add starter variants, NOT main product
        console.log('🎁 [PBFCM-PDP] Product has starter variants, adding only starters:', starterVariants);
        starterVariants.forEach(variantId => {
          items.push(createItemData({
            variantId: parseInt(variantId),
            quantity: state.quantity,
            isSubscription: isSubscription
          }));
        });
      } else {
        // No starter variants - add main product as normal
        console.log('🛒 [PBFCM-PDP] No starter variants, adding main product');
        items.push(createItemData({
          variantId: state.variantId,
          quantity: state.quantity,
          isSubscription: isSubscription
        }));
      }

      // Add upsell products
      Object.keys(state.upsells).forEach(productId => {
        const qty = state.upsells[productId];
        if (qty > 0) {
          const upsellData = document.querySelector(`[data-upsell-id="${productId}"]`);
          if (upsellData) {
              console.log('🛒 [PBFCM-PDP] upsellData', upsellData);

              const variantId = parseInt(upsellData.dataset.variant);
            const priceData = getUpsellPriceData(upsellData);
            const isAddon = upsellData.dataset.isAddon === 'true';
            const upsellProperties = {
              [CONFIG.PROPERTIES.UPSELL_ITEM]: 'true'
            };
            if (priceData.priceCents > 0) {
              upsellProperties[CONFIG.PROPERTIES.BFCM_ADD_ON_PROMO_PRICE] = String(Math.round(priceData.priceCents));
            }
            if (priceData.comparePriceCents > 0) {
              upsellProperties[CONFIG.PROPERTIES.BFCM_ADD_ON_FULL_PRICE] = String(Math.round(priceData.comparePriceCents));
            }

            // Determine selling plan ID based on product title
            let sellingPlanId = null;
            if (isSubscription) {
              const upsellTitle = (upsellData.dataset.upsellTitle || '').toLowerCase();
              const isAddOnProduct = upsellTitle.includes('add-on');
              sellingPlanId = isAddOnProduct ? CONFIG.ADD_ON_SUBSCRIPTION_PLAN_ID : CONFIG.STAY_SELLING_PLAN_ID;
              console.log(`📦 [PBFCM-PDP] Selling plan for "${upsellTitle}": ${isAddOnProduct ? 'ADDON' : 'STAY'} (${sellingPlanId})`);
            }

            items.push(createItemData({
              variantId: variantId,
              quantity: qty,
              isSubscription: isSubscription,
              properties: upsellProperties,
              isAddon: isAddon,
              sellingPlanId: sellingPlanId
            }));
          }
        }
      });

      console.log('🛒 [PBFCM-PDP] Items to add:', items);

      // Add to cart using existing cart API
      if (typeof window.addToCart === 'function') {
        const response = await window.addToCart({ items });
        console.log('✅ [PBFCM-PDP] Add to cart response:', response);

        // Update cart display
        await updateCartAfterAdd();
      } else {
        console.error('❌ [PBFCM-PDP] window.addToCart not available');
        // Fallback: redirect to cart
        window.location.href = '/cart';
      }
    } catch (error) {
      console.error('❌ [PBFCM-PDP] Error adding to cart:', error);
      alert('Error adding product to cart. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  /**
   * Update cart display after adding items
   * Uses CartUtilities if available, otherwise falls back to local implementation
   */
  async function updateCartAfterAdd() {
    console.log('🛒 [PBFCM-PDP] Updating cart display');

    // Use CartUtilities if available
    if (window.CartUtilities && typeof window.CartUtilities.updateCartAfterAdd === 'function') {
      return window.CartUtilities.updateCartAfterAdd();
    }

    // Fallback to local implementation
    try {
      // Fetch updated cart data
      const cartResponse = await fetch('/cart.js');
      const updatedCart = await cartResponse.json();

      console.log('🛒 [PBFCM-PDP] Updated cart:', updatedCart);

      // Open cart drawer
      if (typeof toggleCartOpen === 'function') {
        console.log('🛒 [PBFCM-PDP] Opening cart drawer');
        toggleCartOpen();

        // Focus the checkout button after cart opens for accessibility
        setTimeout(() => {
          const checkoutButton = document.getElementById('checkoutButton');

          if (checkoutButton && !checkoutButton.disabled) {
            checkoutButton.focus();
            console.log('♿ [PBFCM-PDP] Focused checkout button for accessibility');

            // Announce to screen readers
            if (typeof announceToScreenReader === 'function') {
              announceToScreenReader('Items added to cart. Focus on checkout button.', 'polite');
            }
          } else {
            // Fallback: focus close button if checkout is disabled
            const cart = document.querySelector('#main-nav');
            const closeButton = cart?.querySelector('.js-menu__close');

            if (closeButton) {
              closeButton.focus();
              console.log('♿ [PBFCM-PDP] Checkout disabled, focused close button');
            }
          }
        }, 150);
      }

      // Update cart display
      if (typeof updateCartDisplay === 'function') {
        console.log('🛒 [PBFCM-PDP] Updating cart display');
        updateCartDisplay(updatedCart);
      }

      // Update cart count
      if (typeof updateCartCount === 'function') {
        console.log('🛒 [PBFCM-PDP] Updating cart count:', updatedCart.item_count);
        updateCartCount(updatedCart.item_count);
      }

      // Dispatch cart:updated event for gift progress and other components
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: updatedCart } }));
      console.log('📢 [PBFCM-PDP] Dispatched cart:updated event');
    } catch (error) {
      console.error('❌ [PBFCM-PDP] Error updating cart display:', error);
    }
  }

  /**
   * Try Before You Buy - Add to cart handler
   * Uses CartUtilities.addToCartTBYB
   */
  async function handleAddToCartTBYB() {
    console.log('🧪 [PBFCM-PDP] Starting TBYB add to cart');

    // Show loading state
    setLoading(true);

    try {
      const isSubscription = state.frequency === 'monthly';

      // Check if CartUtilities is available
      if (window.CartUtilities && typeof window.CartUtilities.addToCartTBYB === 'function') {
        await window.CartUtilities.addToCartTBYB({
          variantId: state.variantId,
          quantity: state.quantity,
          isSubscription: isSubscription,
          productId: state.productId
        });
      } else {
        console.error('❌ [PBFCM-PDP] CartUtilities.addToCartTBYB not available');
        alert('TBYB functionality is not available. Please try regular add to cart.');
      }
    } catch (error) {
      console.error('❌ [PBFCM-PDP] Error adding TBYB to cart:', error);
      alert('Error adding product to cart. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  /**
   * Set loading state
   * @param {boolean} loading - Whether loading or not
   */
  function setLoading(loading) {
    const btn = document.getElementById(CONFIG.IDS.ADD_TO_CART_BTN);
    const loadingEl = document.getElementById(CONFIG.IDS.CART_LOADING);

    if (btn) {
      btn.disabled = loading;
      btn.style.opacity = loading ? '0.7' : '1';
      btn.style.cursor = loading ? 'not-allowed' : 'pointer';
    }

    if (loadingEl) {
      loadingEl.style.display = loading ? 'flex' : 'none';
    }
  }

  // Expose public API
  PBFCM_PDP.init = init;
  PBFCM_PDP.selectFrequency = selectFrequency;
  PBFCM_PDP.selectQuantity = selectQuantity;
  PBFCM_PDP.updateUpsellQuantity = updateUpsellQuantity;
  PBFCM_PDP.calculateTotals = calculateTotals;
  PBFCM_PDP.handleAddToCart = handleAddToCart;
  PBFCM_PDP.handleAddToCartTBYB = handleAddToCartTBYB;
  PBFCM_PDP.copyCouponCode = copyCouponCode;
  PBFCM_PDP.getState = () => ({ ...state }); // For debugging

})(window.PBFCM_PDP);

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', window.PBFCM_PDP.init);
} else {
  window.PBFCM_PDP.init();
}
